import React, { useEffect, useState } from "react";
import { api, type Me } from "./api";
import { formatPhone } from "../server/phone";

interface UserRow {
  userKey: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  licensed: boolean;
  mayAdministerOtc: boolean;
  credential: string;
  active: boolean;
  lockState: "None" | "Locked" | "Invalidated";
  failedAttempts: number;
  lastLoginUtc: string;
}
interface RoleOpt {
  value: string;
  label: string;
}

function eastern(iso: string): string {
  if (!iso) return "Never";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

/** A PIN shown ONCE to the admin, to give to the person out of band. */
function PinReveal({ who, pin, onClose }: { who: string; pin: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-lg font-semibold">PIN for {who}</h3>
        <p className="my-4 text-center font-mono text-4xl tracking-[0.4em]" data-testid="pin-value">
          {pin}
        </p>
        <p className="text-sm text-slate-700">
          Shown once. Give it to them in person or by phone — never by email. You can rotate it later if needed.
        </p>
        <button type="button" className="btn-primary mt-4 w-full" onClick={onClose}>
          I've given it to them
        </button>
      </div>
    </div>
  );
}

function YesNo({ v }: { v: boolean }) {
  return <span className={v ? "text-slate-900" : "text-slate-400"}>{v ? "Yes" : "No"}</span>;
}

function LockBadge({ s }: { s: UserRow["lockState"] }) {
  if (s === "None") return <span className="text-slate-500">—</span>;
  const cls = s === "Locked" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-900";
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{s}</span>;
}

function UserForm({
  roles,
  initial,
  creating,
  onSave,
  onCancel,
}: {
  roles: RoleOpt[];
  initial: Partial<UserRow>;
  creating: boolean;
  onSave: (v: Partial<UserRow>) => Promise<string | null>;
  onCancel: () => void;
}) {
  const [v, setV] = useState<Partial<UserRow>>(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof UserRow, val: unknown) => setV((o) => ({ ...o, [k]: val }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(await onSave(v));
    setBusy(false);
  }

  return (
    <form onSubmit={submit} className="section-card" noValidate>
      <h3 className="text-base font-semibold mb-3">{creating ? "Add a user" : `Edit ${initial.name}`}</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="u-name">Name</label>
          <input id="u-name" className="field-input" value={v.name ?? ""} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className="field-label" htmlFor="u-email">Email</label>
          <input
            id="u-email"
            className="field-input"
            type="email"
            autoCapitalize="none"
            value={v.email ?? ""}
            disabled={!creating}
            onChange={(e) => set("email", e.target.value)}
          />
          {!creating && <p className="field-hint">A different email is a different user.</p>}
        </div>
        <div>
          <label className="field-label" htmlFor="u-role">Role</label>
          <select
            id="u-role"
            className="field-input"
            value={v.role ?? ""}
            onChange={(e) => {
              const role = e.target.value;
              set("role", role);
              // Plan §5: a NEW RN or Supervisor account starts Licensed. Only on
              // create; editing a role never silently changes the flag.
              if (creating) set("licensed", role === "RN" || role === "Supervisor");
            }}
          >
            <option value="" disabled>Choose…</option>
            {roles.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="u-cred">Credential</label>
          <input
            id="u-cred"
            className="field-input"
            placeholder="e.g. RN, CRNP"
            value={v.credential ?? ""}
            onChange={(e) => set("credential", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="u-phone">Cell phone</label>
          <input
            id="u-phone"
            className="field-input"
            type="tel"
            inputMode="tel"
            placeholder="(410) 555-0123"
            value={v.phone ?? ""}
            onChange={(e) => set("phone", e.target.value)}
          />
          <p className="field-hint">US number. Blank removes it.</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={!!v.licensed} onChange={(e) => set("licensed", e.target.checked)} /> Licensed
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={!!v.mayAdministerOtc} onChange={(e) => set("mayAdministerOtc", e.target.checked)} />{" "}
          May give OTC
        </label>
        {!creating && (
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={!!v.active} onChange={(e) => set("active", e.target.checked)} /> Active
          </label>
        )}
      </div>
      {creating && <p className="field-hint mt-2">The app assigns a random 4-digit PIN and shows it to you once.</p>}
      {error && <p className="field-error">{error}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="submit" className="btn-primary" disabled={busy}>{busy ? "Saving…" : creating ? "Add user" : "Save"}</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function UnlockDialog({ user, onDone, onCancel }: { user: UserRow; onDone: (pin?: string) => void; onCancel: () => void }) {
  const [adminPin, setAdminPin] = useState("");
  const [rotate, setRotate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await api<{ pin?: string }>("POST", `/api/users/${user.userKey}/unlock`, { adminPin, rotate });
    setBusy(false);
    if (r.ok) onDone(r.data.pin);
    else {
      setAdminPin("");
      setError(r.data.error ?? "Unlock failed.");
    }
  }

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true">
      <form onSubmit={submit} className="w-full max-w-sm rounded-lg bg-white p-5 shadow-lg" noValidate>
        <h3 className="text-lg font-semibold">Unlock {user.name}</h3>
        <p className="mt-1 text-sm text-slate-700">Confirm with them first. Their current sign-in link will work again.</p>
        <label className="field-label mt-4" htmlFor="admin-pin">Your own PIN</label>
        <input
          id="admin-pin"
          className="field-input text-center font-mono text-xl tracking-[0.4em] max-w-[9rem]"
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          autoComplete="off"
          value={adminPin}
          onChange={(e) => setAdminPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
        />
        <label className="mt-3 inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={rotate} onChange={(e) => setRotate(e.target.checked)} /> Also rotate their PIN
        </label>
        {error && <p className="field-error" role="alert">{error}</p>}
        <div className="mt-4 flex gap-2">
          <button type="submit" className="btn-primary" disabled={busy || adminPin.length !== 4}>{busy ? "Unlocking…" : "Unlock"}</button>
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

export function Users({ me }: { me: Me }): React.ReactElement {
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [roles, setRoles] = useState<RoleOpt[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<UserRow | "new" | null>(null);
  const [unlocking, setUnlocking] = useState<UserRow | null>(null);
  const [reveal, setReveal] = useState<{ who: string; pin: string } | null>(null);

  async function load() {
    const r = await api<{ users: UserRow[]; roles: RoleOpt[] }>("GET", "/api/users");
    if (r.ok) {
      setUsers([...r.data.users].sort((a, b) => a.name.localeCompare(b.name)));
      setRoles(r.data.roles);
    } else setError(r.data.error ?? "Couldn't load users.");
  }
  useEffect(() => {
    void load();
  }, []);

  async function save(v: Partial<UserRow>): Promise<string | null> {
    const body = {
      name: v.name,
      phone: v.phone ?? "",
      role: v.role,
      credential: v.credential ?? "",
      licensed: !!v.licensed,
      mayAdministerOtc: !!v.mayAdministerOtc,
      ...(editing === "new" ? { email: v.email } : { active: !!v.active }),
    };
    const r =
      editing === "new"
        ? await api<{ pin: string; user: UserRow }>("POST", "/api/users", body)
        : await api("PATCH", `/api/users/${(editing as UserRow).userKey}`, body);
    if (!r.ok) return r.data.error ?? "Save failed.";
    if (editing === "new") setReveal({ who: v.name ?? "", pin: (r.data as { pin: string }).pin });
    setEditing(null);
    await load();
    return null;
  }

  async function rotate(u: UserRow) {
    if (!window.confirm(`Rotate ${u.name}'s PIN? Their current sessions will end.`)) return;
    const r = await api<{ pin: string }>("POST", `/api/users/${u.userKey}/rotate-pin`);
    if (r.ok) setReveal({ who: u.name, pin: r.data.pin });
    else setError(r.data.error ?? "Rotate failed.");
    await load();
  }

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Users</h2>
        {!editing && (
          <button type="button" className="btn-primary" onClick={() => setEditing("new")}>
            Add user
          </button>
        )}
      </div>
      {error && <p className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</p>}
      {editing && (
        <UserForm
          roles={roles}
          creating={editing === "new"}
          initial={
            editing === "new"
              ? { licensed: false, mayAdministerOtc: false, active: true }
              : { ...editing, phone: formatPhone(editing.phone) }
          }
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}
      {!users ? (
        <p className="text-sm text-slate-600">Loading…</p>
      ) : (
        <>
          {/* Phone and tablet: cards */}
          <ul className="space-y-3 lg:hidden">
            {users.map((u) => (
              <li key={u.userKey} className={`rounded-lg border bg-white p-4 shadow-sm ${u.active ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{u.name}{u.userKey === me.userKey && <span className="text-slate-500"> (you)</span>}</p>
                    <p className="text-sm text-slate-600 break-all">{u.email}</p>
                  </div>
                  <LockBadge s={u.lockState} />
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                  <dt className="text-slate-500">Role</dt><dd>{roles.find((r) => r.value === u.role)?.label ?? u.role}</dd>
                  <dt className="text-slate-500">Credential</dt><dd>{u.credential || "—"}</dd>
                  <dt className="text-slate-500">Phone</dt><dd className="whitespace-nowrap">{formatPhone(u.phone) || "—"}</dd>
                  <dt className="text-slate-500">Licensed</dt><dd><YesNo v={u.licensed} /></dd>
                  <dt className="text-slate-500">May give OTC</dt><dd><YesNo v={u.mayAdministerOtc} /></dd>
                  <dt className="text-slate-500">Active</dt><dd><YesNo v={u.active} /></dd>
                  <dt className="text-slate-500">Last sign-in</dt><dd>{eastern(u.lastLoginUtc)}</dd>
                </dl>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" className="btn-secondary text-sm" onClick={() => setEditing(u)}>Edit</button>
                  <button type="button" className="btn-secondary text-sm" onClick={() => rotate(u)}>Rotate PIN</button>
                  {u.lockState === "Locked" && (
                    <button type="button" className="btn-primary text-sm" onClick={() => setUnlocking(u)}>Unlock</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {/* Desktop: table */}
          <div className="hidden lg:block rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  {["Name", "Phone", "Role", "Credential", "Licensed", "OTC", "Active", "Lock", "Last sign-in", ""].map((h) => (
                    <th key={h} className="px-3 py-2 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.userKey} className={u.active ? "" : "opacity-60"}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{u.name}{u.userKey === me.userKey && <span className="text-slate-500"> (you)</span>}</div>
                      <div className="text-slate-500 break-all">{u.email}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatPhone(u.phone) || "—"}</td>
                    <td className="px-3 py-2">{roles.find((r) => r.value === u.role)?.label ?? u.role}</td>
                    <td className="px-3 py-2">{u.credential || "—"}</td>
                    <td className="px-3 py-2"><YesNo v={u.licensed} /></td>
                    <td className="px-3 py-2"><YesNo v={u.mayAdministerOtc} /></td>
                    <td className="px-3 py-2"><YesNo v={u.active} /></td>
                    <td className="px-3 py-2"><LockBadge s={u.lockState} /></td>
                    <td className="px-3 py-2 whitespace-nowrap">{eastern(u.lastLoginUtc)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col items-start gap-1 whitespace-nowrap">
                        <button type="button" className="text-program hover:underline" onClick={() => setEditing(u)}>Edit</button>
                        <button type="button" className="text-program hover:underline" onClick={() => rotate(u)}>Rotate PIN</button>
                        {u.lockState === "Locked" && (
                          <button type="button" className="btn-primary px-2 py-1 text-xs" onClick={() => setUnlocking(u)}>Unlock</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {unlocking && (
        <UnlockDialog
          user={unlocking}
          onCancel={() => setUnlocking(null)}
          onDone={(pin) => {
            const who = unlocking.name;
            setUnlocking(null);
            if (pin) setReveal({ who, pin });
            void load();
          }}
        />
      )}
      {reveal && <PinReveal who={reveal.who} pin={reveal.pin} onClose={() => setReveal(null)} />}
    </section>
  );
}
